import { useCallback, useContext, useMemo } from 'react';

import { sumDataTransferCost, toNumOrZero } from '@cast/utils';

import { Money } from 'components/money';

import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { getNetworkCostPrecision } from 'features/cost-report/utils/networkCosts';
import { useCostReportContext } from '../../../../../cost-report/hooks/useCostReportContext';
import { useCostReportTimeRangeSelection } from '../../../../../cost-report/hooks/useCostReportTimeRangeSelection';

import { NetworkCostOverTimeContext } from '../../../providers/NetworkCostOverTimeProvider';
import { useDataTransferChartDatapoints } from '../../_hooks/useDataTransferChartDatapoints';
import { useDataTransferSummary } from '../../_hooks/useDataTransferSummary';
import { ChartSectionBox } from '../_components/ChartSectionBox';
import { NetworkChart } from '../_components/NetworkChart';
import { makeNetworkChartTooltip } from '../_components/NetworkChartTooltip';
import { NetworkMetricsBox } from '../_components/NetworkMetricsBox';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { Stack } from '@cast/design-system';

const EMPTY_VALUE = '--.--';

export const NetworkCostSection = () => {
  const { cluster } = useCluster();
  const { isDefaultFilter, timezone } = useCostReportContext();
  const { getRange } = useCostReportTimeRangeSelection();
  const { current } = useContext(NetworkCostOverTimeContext);
  const chartData = useDataTransferChartDatapoints('cost');
  const { cost } = useDataTransferSummary();

  const data = current.data.items;
  const accumulated = useMemo(() => {
    if (data.length) {
      if (isDefaultFilter) {
        const today = data[data.length - 1];
        const egress = sumDataTransferCost(today.egress);
        const ingress = sumDataTransferCost(today.ingress);

        const totalPrecision = getNetworkCostPrecision(egress + ingress);
        const egressPrecision = getNetworkCostPrecision(egress);
        const ingressPrecision = getNetworkCostPrecision(ingress);

        return {
          total: egress + ingress,
          totalPrecision,
          egress,
          egressPrecision,
          ingress,
          ingressPrecision,
        };
      } else {
        return {
          total: cost.egress + cost.ingress,
          totalPrecision: getNetworkCostPrecision(cost.egress + cost.ingress),
          egress: cost.egress,
          egressPrecision: getNetworkCostPrecision(cost.egress),
          ingress: cost.ingress,
          ingressPrecision: getNetworkCostPrecision(cost.ingress),
        };
      }
    }
    return undefined;
  }, [data, cost, isDefaultFilter]);

  const tooltipFormatterFn = useCallback((v: number) => {
    const precision = getNetworkCostPrecision(v);
    return <Money amount={v} precision={precision} fixed={precision} />;
  }, []);

  const { tooltipHeader, tooltipFormatValue } = makeNetworkChartTooltip({
    label: 'Network cost',
    timezone,
    getValue: (data) => toNumOrZero(data.egress) + toNumOrZero(data.ingress),
    formatValue: tooltipFormatterFn,
  });

  return (
    <ChartSectionBox>
      <Stack
        display="grid"
        gridTemplateColumns="repeat(2, 1fr)"
        gap={24}
        p="12px 20px"
      >
        <NetworkMetricsBox
          title={isDefaultFilter ? 'current day spend' : 'total spend'}
          primaryValue={
            !current.isLoading && typeof accumulated?.total === 'number' ? (
              <Money
                amount={accumulated.total}
                precision={accumulated.totalPrecision}
                fixed={accumulated.totalPrecision}
              />
            ) : (
              EMPTY_VALUE
            )
          }
        />
        <Stack
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={24}
          px={16}
        >
          <NetworkMetricsBox
            markerColor="indigo.200"
            title="egress"
            primaryValue={
              !current.isLoading && typeof accumulated?.egress === 'number' ? (
                <Money
                  amount={accumulated.egress}
                  precision={accumulated.egressPrecision}
                  fixed={accumulated.egressPrecision}
                />
              ) : (
                EMPTY_VALUE
              )
            }
            primaryValueProps={{ variant: 'h6', lineHeight: '20px' }}
          />
          <NetworkMetricsBox
            markerColor="grey.700"
            title="ingress"
            primaryValue={
              !current.isLoading && typeof accumulated?.ingress === 'number' ? (
                <Money
                  amount={accumulated.ingress}
                  precision={accumulated.ingressPrecision}
                  fixed={accumulated.ingressPrecision}
                />
              ) : (
                EMPTY_VALUE
              )
            }
            primaryValueProps={{ variant: 'h6', lineHeight: '20px' }}
          />
        </Stack>
      </Stack>

      <NetworkChart
        data={chartData}
        isLoading={current.isLoading}
        unit="$"
        tooltipHeader={tooltipHeader}
        tooltipFormatValue={tooltipFormatValue}
        yAxisProps={{
          allowDecimals: true,
          tickFormatter: (t: string | number) => {
            const tickValue = Number(t);
            // disable tick if it's too small
            if (tickValue < 0.000001 && tickValue > 0) {
              return '';
            }

            return `$ ${t}`;
          },
        }}
        timezone={timezone}
        buildPrompt={buildCostReportAnalyzePrompt({
          scopeName: cluster?.name,
          context: 'network cost',
          getDefaultRange: getRange,
        })}
      />
    </ChartSectionBox>
  );
};
